import { useCallback, useEffect, useState } from 'react';
import { IUtilityMapStatus } from 'types';
import { UtilityMapProcessName } from 'utils/helpers';
import { UtilityMapService } from 'utils/services';

const STATUS_POLL_INTERVAL: Record<UtilityMapProcessName, number> = {
	byStreet: 60000,
	byCity: 300000,
	allRejects: 300000,
};

interface UseUtilityMapStatusResult {
	markRunning: (processName: UtilityMapProcessName) => void;
	refresh: () => Promise<void>;
	status: IUtilityMapStatus | null;
}

export const useUtilityMapStatus = (processName: UtilityMapProcessName): UseUtilityMapStatusResult => {
	const [status, setStatus] = useState<IUtilityMapStatus | null>(null);

	const refresh = useCallback(async (): Promise<void> => {
		try {
			const response = await UtilityMapService.getStatus();
			setStatus(response.data);
		} catch {
			// This background status check should not interrupt page usage.
		}
	}, []);

	const markRunning = useCallback((runningProcessName: UtilityMapProcessName): void => {
		setStatus((currentStatus) => {
			if (!currentStatus) {
				return currentStatus;
			}

			return {
				...currentStatus,
				[runningProcessName]: {
					...currentStatus[runningProcessName],
					status: 'running',
				},
			};
		});
	}, []);

	useEffect(() => {
		let isActive = true;
		let timeoutId: number | undefined;

		const loadStatus = async (): Promise<void> => {
			await refresh();

			if (isActive) {
				timeoutId = window.setTimeout(() => {
					void loadStatus();
				}, STATUS_POLL_INTERVAL[processName]);
			}
		};

		void loadStatus();

		return () => {
			isActive = false;

			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [processName, refresh]);

	return { markRunning, refresh, status };
};
